import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  MdPlayArrow,
  MdPause,
  MdSkipPrevious,
  MdSkipNext,
  MdShuffle,
  MdRepeat,
  MdRepeatOne,
  MdVolumeUp,
  MdVolumeOff,
  MdClose,
  MdOpenInFull,
  MdDevices,
  MdUnfoldLess,
  MdUnfoldMore,
} from 'react-icons/md';
import './MusicMiniPlayer.css';
import { normalizeMediaUrl } from './config';

// Helper to format seconds to m:ss or mm:ss
function formatTime(sec) {
  if (!sec || isNaN(sec) || sec < 0) return '0:00';
  const total = Math.floor(sec);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  const padSec = seconds < 10 ? `0${seconds}` : `${seconds}`;
  return `${minutes}:${padSec}`;
}

export default function MusicMiniPlayer({
  isOpen = true,
  musicSession,
  onTogglePlay,
  onSeek,
  onNext,
  onPrev,
  onToggleShuffle,
  onToggleRepeat,
  onSetVolume,
  onToggleMute,
  onClose,
  onNavigate,
}) {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [animClass, setAnimClass] = useState(isOpen ? 'shrink-in' : 'shrink-out');
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [scrubValue, setScrubValue] = useState(0);
  const [isCompact, setIsCompact] = useState(() => {
    try {
      return localStorage.getItem('speedotube_miniplayer_compact') === 'true';
    } catch (e) {
      return false;
    }
  });
  const progressBarRef = useRef(null);
  const exitTimerRef = useRef(null);
  const isExpandingRef = useRef(false);

  // In-place shrink/expand toggle between compact bar and full card
  const toggleCompact = useCallback((e) => {
    e.stopPropagation();
    setIsCompact((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('speedotube_miniplayer_compact', String(next));
      } catch (err) {}
      return next;
    });
  }, []);

  // Synchronize opening and closing with smooth fluid shrinking transitions
  useEffect(() => {
    if (isOpen) {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
      isExpandingRef.current = false;
      setShouldRender(true);
      const raf = requestAnimationFrame(() => {
        setAnimClass('shrink-in');
      });
      return () => cancelAnimationFrame(raf);
    } else {
      // If currently expanding to full Watch Page, don't play reverse shrink-out
      if (isExpandingRef.current) {
        return;
      }
      if (shouldRender) {
        setAnimClass('shrink-out');
        exitTimerRef.current = setTimeout(() => {
          setShouldRender(false);
        }, 260);
        return () => {
          if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
        };
      }
    }
  }, [isOpen, shouldRender]);

  const album = musicSession?.album;
  const currentTrackIndex = musicSession?.currentTrackIndex ?? 0;
  const currentTrack = album?.tracks?.[currentTrackIndex] || album?.tracks?.[0] || {
    title: album?.title || 'Music Track',
    artist: album?.artist || 'Artist',
    durationSec: 243,
  };

  const isPlaying = Boolean(musicSession?.isPlaying);
  const currentTime = isScrubbing ? scrubValue : (musicSession?.currentTime || 0);
  const duration = musicSession?.duration || currentTrack?.durationSec || 243;
  const volume = musicSession?.volume ?? 1;
  const isMuted = Boolean(musicSession?.isMuted);
  const isShuffle = Boolean(musicSession?.isShuffle);
  const repeatMode = musicSession?.repeatMode || 'off'; // 'off' | 'all' | 'one'

  const progressPercent = duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0;
  const remainingTime = Math.max(0, duration - currentTime);

  // Close smoothly with shrink-out into corner
  const handleClose = useCallback((e) => {
    e.stopPropagation();
    setAnimClass('shrink-out');
    setTimeout(() => {
      if (onClose) onClose();
      setShouldRender(false);
    }, 260);
  }, [onClose]);

  // Smooth Expand back to full WatchPage with expanding zoom transition
  const handleExpand = useCallback((e) => {
    e.stopPropagation();
    if (isExpandingRef.current) return;
    isExpandingRef.current = true;
    setAnimClass('expand-out');

    setTimeout(() => {
      if (onNavigate && album) {
        onNavigate(`/watch?album=${album.albumUrlId || album.id}`);
      }
      setTimeout(() => {
        setShouldRender(false);
        isExpandingRef.current = false;
      }, 60);
    }, 280);
  }, [onNavigate, album]);

  // Handle Scrubber Seek
  const handleScrubberMouseDown = (e) => {
    e.stopPropagation();
    if (!progressBarRef.current || duration <= 0) return;
    setIsScrubbing(true);
    const rect = progressBarRef.current.getBoundingClientRect();
    const clickX = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const newTime = (clickX / rect.width) * duration;
    setScrubValue(newTime);

    const handleMouseMove = (moveEvent) => {
      const moveX = Math.max(0, Math.min(rect.width, moveEvent.clientX - rect.left));
      const updatedTime = (moveX / rect.width) * duration;
      setScrubValue(updatedTime);
    };

    const handleMouseUp = (upEvent) => {
      const finalX = Math.max(0, Math.min(rect.width, upEvent.clientX - rect.left));
      const finalTime = (finalX / rect.width) * duration;
      setIsScrubbing(false);
      if (onSeek) onSeek(finalTime);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // Keyboard accessibility
  useEffect(() => {
    const handleKeyDown = (e) => {
      // If typing in input or textarea, skip
      if (['INPUT', 'TEXTAREA'].includes(e.target?.tagName)) return;
      if (e.code === 'Space') {
        // Only if miniplayer is focused
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!album || !shouldRender) {
    return null;
  }

  const coverUrl = normalizeMediaUrl(album.coverUrl) || (process.env.PUBLIC_URL || '') + '/img/banner.png';

  return (
    <aside
      className={`speedo-miniplayer-floating-card ${animClass} ${isCompact ? 'is-compact' : ''}`}
      onClick={isCompact ? toggleCompact : handleExpand}
      aria-label="Floating Music Miniplayer"
      role="region"
    >
      {isCompact ? (
        /* Compact / Shrunk Bar Layout */
        <div className="speedo-miniplayer-compact-wrap">
          <div className="speedo-miniplayer-compact-thumb-wrap" onClick={toggleCompact}>
            <img
              src={coverUrl}
              alt={album.title || 'Album Cover'}
              className="speedo-miniplayer-compact-thumb"
              onError={(e) => {
                e.currentTarget.src = (process.env.PUBLIC_URL || '') + '/img/banner.png';
              }}
            />
          </div>

          <div className="speedo-miniplayer-compact-meta" onClick={toggleCompact}>
            <span className="speedo-miniplayer-compact-title">{currentTrack.title || album.title}</span>
            <span className="speedo-miniplayer-compact-artist">{currentTrack.artist || album.artist || 'Pratham'}</span>
          </div>

          <div className="speedo-miniplayer-compact-actions" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="speedo-miniplayer-compact-play-btn"
              onClick={onTogglePlay}
              data-miniplayer-tooltip={isPlaying ? 'Pause' : 'Play'}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <MdPause size={18} color="#000" /> : <MdPlayArrow size={18} color="#000" style={{ marginLeft: '1px' }} />}
            </button>
            <button
              type="button"
              className="speedo-miniplayer-action-icon-btn"
              onClick={toggleCompact}
              data-miniplayer-tooltip="Expand to full card"
              aria-label="Expand card"
            >
              <MdUnfoldMore size={16} />
            </button>
            <button
              type="button"
              className="speedo-miniplayer-action-icon-btn"
              onClick={handleExpand}
              data-miniplayer-tooltip="Open album"
              aria-label="Expand player"
            >
              <MdOpenInFull size={14} />
            </button>
            <button
              type="button"
              className="speedo-miniplayer-action-icon-btn close-btn"
              onClick={handleClose}
              data-miniplayer-tooltip="Close"
              data-tooltip-align="right"
              aria-label="Close miniplayer"
            >
              <MdClose size={15} />
            </button>
          </div>

          <div className="speedo-miniplayer-compact-progress-track">
            <div className="speedo-miniplayer-compact-progress-fill" style={{ width: `${progressPercent}%` }} />
          </div>
        </div>
      ) : (
        /* Full Expanded Card Layout */
        <div className="speedo-miniplayer-full-wrap">
          {/* Top Header Row: Device status badge & Window actions */}
          <div className="speedo-miniplayer-header" onClick={(e) => e.stopPropagation()}>
            <div className="speedo-miniplayer-device-badge">
              <span className={`speedo-miniplayer-live-dot ${isPlaying ? 'playing' : ''}`} />
              <span className="speedo-miniplayer-device-text">SpeedoTube Music</span>
            </div>

            <div className="speedo-miniplayer-header-actions">
              <button
                type="button"
                className="speedo-miniplayer-action-icon-btn"
                onClick={toggleCompact}
                data-miniplayer-tooltip="Collapse to bar"
                aria-label="Shrink miniplayer"
              >
                <MdUnfoldLess size={16} />
              </button>
              <button
                type="button"
                className="speedo-miniplayer-action-icon-btn"
                onClick={handleExpand}
                data-miniplayer-tooltip="Open album"
                aria-label="Expand player"
              >
                <MdOpenInFull size={15} />
              </button>
              <button
                type="button"
                className="speedo-miniplayer-action-icon-btn close-btn"
                onClick={handleClose}
                data-miniplayer-tooltip="Close"
                data-tooltip-align="right"
                aria-label="Close miniplayer"
              >
                <MdClose size={16} />
              </button>
            </div>
          </div>

      {/* Album Artwork Cover */}
      <div className="speedo-miniplayer-cover-box" onClick={handleExpand}>
        <img
          src={coverUrl}
          alt={album.title || 'Album Cover'}
          className="speedo-miniplayer-cover-img"
          onError={(e) => {
            e.currentTarget.src = (process.env.PUBLIC_URL || '') + '/img/banner.png';
          }}
        />
        <div className="speedo-miniplayer-cover-overlay">
          <span className="speedo-miniplayer-expand-hint">Click to open album</span>
        </div>
      </div>

      {/* Track Meta Information */}
      <div className="speedo-miniplayer-meta" onClick={handleExpand}>
        <div className="speedo-miniplayer-device-sublabel">Device</div>
        <h4 className="speedo-miniplayer-track-title" title={currentTrack.title}>
          {currentTrack.title || album.title}
        </h4>
        <p className="speedo-miniplayer-artist-name" title={currentTrack.artist || album.artist}>
          {currentTrack.artist || album.artist || 'Pratham'}
        </p>
      </div>

      {/* Live Scrubber / Progress Bar */}
      <div
        className="speedo-miniplayer-scrubber-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          ref={progressBarRef}
          className="speedo-miniplayer-progress-bar"
          onMouseDown={handleScrubberMouseDown}
        >
          <div
            className="speedo-miniplayer-progress-fill"
            style={{ width: `${progressPercent}%` }}
          >
            <div className="speedo-miniplayer-progress-thumb" />
          </div>
        </div>

        <div className="speedo-miniplayer-time-row">
          <span className="speedo-miniplayer-time-text">{formatTime(currentTime)}</span>
          <span className="speedo-miniplayer-time-text">-{formatTime(remainingTime)}</span>
        </div>
      </div>

      {/* Media Playback Controls Row */}
      <div
        className="speedo-miniplayer-controls"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Shuffle Button */}
        <button
          type="button"
          className={`speedo-miniplayer-ctrl-btn ${isShuffle ? 'active' : ''}`}
          onClick={onToggleShuffle}
          data-miniplayer-tooltip={isShuffle ? 'Disable Shuffle' : 'Enable Shuffle'}
          data-tooltip-align="left"
          aria-label="Shuffle"
        >
          <MdShuffle size={18} />
        </button>

        {/* Previous Track Button */}
        <button
          type="button"
          className="speedo-miniplayer-ctrl-btn"
          onClick={onPrev}
          data-miniplayer-tooltip="Previous"
          aria-label="Previous"
        >
          <MdSkipPrevious size={26} />
        </button>

        {/* Center Play / Pause Pill/Circle Button */}
        <button
          type="button"
          className={`speedo-miniplayer-play-btn ${isPlaying ? 'playing' : ''}`}
          onClick={onTogglePlay}
          data-miniplayer-tooltip={isPlaying ? 'Pause' : 'Play'}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <MdPause size={22} color="#000000" />
          ) : (
            <MdPlayArrow size={24} color="#000000" style={{ marginLeft: '2px' }} />
          )}
        </button>

        {/* Next Track Button */}
        <button
          type="button"
          className="speedo-miniplayer-ctrl-btn"
          onClick={onNext}
          data-miniplayer-tooltip="Next"
          aria-label="Next"
        >
          <MdSkipNext size={26} />
        </button>

        {/* Repeat Button */}
        <button
          type="button"
          className={`speedo-miniplayer-ctrl-btn ${repeatMode !== 'off' ? 'active' : ''}`}
          onClick={onToggleRepeat}
          data-miniplayer-tooltip={
            repeatMode === 'one'
              ? 'Repeat One Active'
              : repeatMode === 'all'
              ? 'Repeat All Active'
              : 'Repeat Off'
          }
          data-tooltip-align="right"
          aria-label="Repeat"
        >
          {repeatMode === 'one' ? <MdRepeatOne size={19} /> : <MdRepeat size={18} />}
        </button>
      </div>

      {/* Volume Slider Row */}
      <div
        className="speedo-miniplayer-volume-row"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="speedo-miniplayer-volume-track-wrap">
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={isMuted ? 0 : volume}
            onChange={(e) => {
              if (onSetVolume) onSetVolume(parseFloat(e.target.value));
            }}
            className="speedo-miniplayer-volume-slider"
            style={{
              background: `linear-gradient(to right, #ffffff ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.2) ${(isMuted ? 0 : volume) * 100}%)`,
            }}
            aria-label="Volume slider"
          />
        </div>

        <button
          type="button"
          className="speedo-miniplayer-volume-btn"
          onClick={onToggleMute}
          data-miniplayer-tooltip={isMuted || volume === 0 ? 'Unmute' : 'Mute'}
          data-tooltip-align="right"
          aria-label="Mute toggle"
        >
          {isMuted || volume === 0 ? (
            <MdVolumeOff size={18} color="rgba(255,255,255,0.7)" />
          ) : (
            <MdVolumeUp size={18} color="rgba(255,255,255,0.7)" />
          )}
        </button>
      </div>

      {/* Subtle Spotify-style Devices Available Footer */}
      <div
        className="speedo-miniplayer-devices-footer"
        onClick={handleExpand}
        data-miniplayer-tooltip="SpeedoTube Audio Sync Active"
      >
        <MdDevices size={13} className="speedo-miniplayer-devices-icon" />
        <span className="speedo-miniplayer-devices-text">Devices Available</span>
      </div>
    </div>
  )}
</aside>
  );
}
